import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiX, FiPhone, FiMail, FiCalendar, FiHash,
  FiPlus, FiTrash2, FiFileText, FiPaperclip,
  FiCheckSquare, FiInfo, FiCheck, FiClipboard, FiUserCheck,
} from 'react-icons/fi';
import {
  listarProcessosOnboard,
  vincularEtapa,
  adicionarProcesso,
  toggleProcesso,
  removerProcesso,
  listarEtapasPadrao,
  salvarRegistro,
} from '../../../../services/etapas';
import DocumentoModal from './DocumentoModal';
import CadastroClienteModal from './CadastroClienteModal';
import AnexoModal from './AnexoModal';
import styles from '../Onboard.module.css';

/* lock de módulo — persiste entre remontagens (StrictMode) */
const syncandoLeads = new Set();

function formatarData(iso) {
  if (!iso) return '';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

/* ===== Sub-modal: Possui Registro? ===== */

function RegistroModal({ processo, lead, onSalvar, onCancelar }) {
  const [possui,         setPossui]         = useState(null); // null | true | false
  const [nomeCampo,      setNomeCampo]      = useState('');
  const [valorRegistro,  setValorRegistro]  = useState('');
  const [justificativa,  setJustificativa]  = useState('');
  const [salvando,       setSalvando]       = useState(false);
  const [erro,           setErro]           = useState('');

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onCancelar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancelar]);

  async function handleSalvar() {
    setErro('');
    if (possui === null)                         return setErro('Selecione Sim ou Não');
    if (possui && !nomeCampo.trim())             return setErro('Informe o nome do registro');
    if (possui && !valorRegistro.trim())         return setErro('Informe o número / registro');
    if (!possui && !justificativa.trim())        return setErro('Informe a justificativa');

    setSalvando(true);
    try {
      await onSalvar({ possui, nomeCampo, valorRegistro, justificativa });
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }

  return (
    <div className={styles.registroOverlay}>
      <div className={styles.registroModal}>
        {/* Header */}
        <div className={styles.registroHeader}>
          <FiClipboard size={16} className={styles.registroHeaderIcon} />
          <div>
            <p className={styles.registroTitulo}>Possui este registro?</p>
            <p className={styles.registroSubtitulo}>{processo.titulo}</p>
          </div>
          <button className={styles.btnFechar} onClick={onCancelar} style={{ marginLeft: 'auto' }}>
            <FiX size={16} />
          </button>
        </div>

        {/* Opções Sim / Não */}
        <div className={styles.registroOpcoes}>
          <button
            className={`${styles.registroOpcao} ${possui === true  ? styles.registroOpcaoSim  : ''}`}
            onClick={() => setPossui(true)}
          >
            <span className={styles.registroOpcaoCircle}>
              {possui === true && <FiCheck size={11} strokeWidth={3} />}
            </span>
            Sim
          </button>
          <button
            className={`${styles.registroOpcao} ${possui === false ? styles.registroOpcaoNao  : ''}`}
            onClick={() => setPossui(false)}
          >
            <span className={styles.registroOpcaoCircle}>
              {possui === false && <FiCheck size={11} strokeWidth={3} />}
            </span>
            Não
          </button>
        </div>

        {/* Campos condicionais */}
        {possui === true && (
          <div className={styles.registroForm}>
            <div className={styles.registroCampo}>
              <label className={styles.registroLabel}>Nome do registro</label>
              <input
                className={styles.registroInput}
                placeholder="Ex: Contrato Social, Inscrição Estadual..."
                value={nomeCampo}
                onChange={e => setNomeCampo(e.target.value)}
                autoFocus
              />
            </div>
            <div className={styles.registroCampo}>
              <label className={styles.registroLabel}>Número / Registro</label>
              <input
                className={styles.registroInput}
                placeholder="Número, código ou referência..."
                value={valorRegistro}
                onChange={e => setValorRegistro(e.target.value)}
              />
            </div>
          </div>
        )}

        {possui === false && (
          <div className={styles.registroForm}>
            <div className={styles.registroCampo}>
              <label className={styles.registroLabel}>Justificativa</label>
              <textarea
                className={styles.registroTextarea}
                placeholder="Por que não possui este registro?"
                value={justificativa}
                onChange={e => setJustificativa(e.target.value)}
                rows={3}
                autoFocus
              />
            </div>
          </div>
        )}

        {erro && <p className={styles.registroErro}>{erro}</p>}

        {/* Footer */}
        <div className={styles.registroFooter}>
          <button
            className={styles.registroBtnCancelar}
            onClick={onCancelar}
            disabled={salvando}
          >
            Cancelar
          </button>
          <button
            className={styles.registroBtnSalvar}
            onClick={handleSalvar}
            disabled={salvando || possui === null}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===== Item do checklist ===== */

function ChecklistItem({ proc, onToggle, onRemover, onVerDocumento, templateTexto, abrirCadastro, onAbrirCadastro, requerAnexo, onAbrirAnexo }) {
  return (
    <div className={`${styles.checklistRow} ${proc.concluida ? styles.checklistRowConcluida : ''}`}>
      <span className={styles.checklistHandle} aria-hidden>⠿</span>
      <button
        type="button"
        className={`${styles.checkCircleBtn} ${proc.concluida ? styles.checkCircleAtivo : ''}`}
        onClick={() => onToggle(proc.id)}
        aria-label={proc.concluida ? 'Desmarcar' : 'Concluir'}
      >
        {proc.concluida && <FiCheck size={11} color="#fff" strokeWidth={3} />}
      </button>
      <span className={styles.checklistItemDesc}>{proc.titulo}</span>
      <div className={styles.checklistAcoes}>
        {templateTexto && (
          <button
            type="button"
            className={styles.checklistBtnDoc}
            onClick={() => onVerDocumento(proc.titulo, templateTexto)}
            title="Ver documento"
          >
            <FiFileText size={13} />
          </button>
        )}
        <button
          type="button"
          className={styles.checklistBtnDelete}
          onClick={() => onRemover(proc.id)}
          aria-label="Remover"
        >
          <FiTrash2 size={14} />
        </button>
        {abrirCadastro && (
          <button
            type="button"
            className={styles.checklistBtnCadastro}
            onClick={onAbrirCadastro}
            title="Abrir cadastro do cliente"
          >
            <FiUserCheck size={14} />
          </button>
        )}
        {requerAnexo && (
          <button
            type="button"
            className={`${styles.checklistBtnAnexo} ${proc.anexo_url ? styles.checklistBtnAnexoAtivo : ''}`}
            onClick={onAbrirAnexo}
            title={proc.anexo_url ? `Documento: ${proc.anexo_nome || 'Anexo'}` : 'Anexar documento obrigatório'}
          >
            <FiPaperclip size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ===== Modal principal full-screen ===== */

function DetalheModal({ lead: leadInicial, onFechar, onLeadAtualizado }) {
  const [lead,           setLead]           = useState(leadInicial);
  const [abaAtiva,       setAbaAtiva]       = useState('checklist');
  const [processos,      setProcessos]      = useState([]);
  const [etapasPadrao,   setEtapasPadrao]   = useState([]);
  const [carregando,     setCarregando]     = useState(true);
  const [novoProcesso,   setNovoProcesso]   = useState('');
  const [salvando,       setSalvando]       = useState(false);
  const [erro,           setErro]           = useState('');
  const [documento,      setDocumento]      = useState(null);
  const [registroModal,  setRegistroModal]  = useState(null); // { proc, etapa }
  const [cadastroAberto, setCadastroAberto] = useState(false);
  const [anexoProc,      setAnexoProc]      = useState(null); // proc object

  const onLeadAtualizadoRef = useRef(onLeadAtualizado);
  onLeadAtualizadoRef.current = onLeadAtualizado;

  useEffect(() => {
    function onKey(e) {
      /* Não fechar o fullscreen quando o RegistroModal estiver aberto */
      if (e.key === 'Escape' && !registroModal) onFechar();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar, registroModal]);

  const carregarDados = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [procs, etapas] = await Promise.all([
        listarProcessosOnboard(leadInicial.id),
        listarEtapasPadrao(),
      ]);
      setEtapasPadrao(etapas);

      const naoConcludo = (leadInicial.onboardStatus || 'pendente') !== 'concluido';
      const etapasComProcessos = etapas.filter(e => e.processos?.length > 0);

      if (naoConcludo && etapasComProcessos.length > 0 && !syncandoLeads.has(leadInicial.id)) {
        syncandoLeads.add(leadInicial.id);
        try {
          let ultimoResult = null;
          for (const etapa of etapasComProcessos) {
            try { ultimoResult = await vincularEtapa(leadInicial.id, etapa.id); }
            catch { /* ignora erros individuais */ }
          }
          const todosProcessos = await listarProcessosOnboard(leadInicial.id);
          setProcessos(todosProcessos);
          if (ultimoResult) {
            const atualizado = {
              ...leadInicial,
              onboardProgresso: ultimoResult.progresso,
              onboardStatus:    ultimoResult.onboard_status,
            };
            setLead(atualizado);
            onLeadAtualizadoRef.current?.(atualizado);
          }
        } finally {
          setTimeout(() => syncandoLeads.delete(leadInicial.id), 2000);
        }
        return;
      }

      setProcessos(procs);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadInicial.id]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  function atualizarLead(progresso, status) {
    const atualizado = { ...lead, onboardProgresso: progresso, onboardStatus: status };
    setLead(atualizado);
    onLeadAtualizadoRef.current?.(atualizado);
  }

  async function doToggle(processoId) {
    try {
      const result = await toggleProcesso(processoId);
      setProcessos(prev => prev.map(p => p.id === processoId ? result.processo : p));
      atualizarLead(result.progresso, result.onboard_status);
    } catch (e) { setErro(e.message); }
  }

  function handleToggle(processoId) {
    const proc = processos.find(p => p.id === processoId);
    if (!proc) return;

    /* Se já está concluído → apenas desmarcar (sem modal) */
    if (proc.concluida) {
      doToggle(processoId);
      return;
    }

    const etapa = etapasPadrao.find(e => e.id === proc.etapa_padrao_id);
    const pp    = etapa?.processos?.find(p => p.id === proc.processo_padrao_id);

    /* Bloqueia se requer anexo e ainda não tem */
    if (pp?.requer_anexo && !proc.anexo_url) {
      setAnexoProc(proc);
      return;
    }

    /* Verificar se a etapa exige registro */
    if (etapa?.requer_registro) {
      setRegistroModal({ proc, etapa });
    } else {
      doToggle(processoId);
    }
  }

  function handleAnexoSalvo(processoId, { anexo_url, anexo_nome }) {
    setProcessos(prev => prev.map(p =>
      p.id === processoId ? { ...p, anexo_url, anexo_nome } : p
    ));
    setAnexoProc(null);
  }

  async function handleSalvarRegistro({ possui, nomeCampo, valorRegistro, justificativa }) {
    const proc = registroModal.proc;
    await salvarRegistro(lead.id, {
      onboard_processo_id: proc.id,
      cnpj:                lead.cnpj || '',
      possui,
      nome_campo:          nomeCampo,
      valor_registro:      valorRegistro,
      justificativa,
    });
    setRegistroModal(null);
    await doToggle(proc.id);
  }

  async function handleRemover(processoId) {
    try {
      const result = await removerProcesso(processoId);
      setProcessos(prev => prev.filter(p => p.id !== processoId));
      atualizarLead(result.progresso, result.onboard_status);
    } catch (e) { setErro(e.message); }
  }

  async function handleAdicionarCustom() {
    if (!novoProcesso.trim()) return;
    setSalvando(true);
    try {
      const result = await adicionarProcesso(lead.id, novoProcesso.trim());
      setProcessos(prev => [...prev, result.processo]);
      atualizarLead(result.progresso, result.onboard_status);
      setNovoProcesso('');
    } catch (e) { setErro(e.message); }
    finally { setSalvando(false); }
  }

  const tipo       = lead.empresa ? 'CNPJ' : 'CPF';
  const total      = processos.length;
  const concluidos = processos.filter(p => p.concluida).length;
  const progresso  = lead.onboardProgresso ?? 0;

  const gruposMap   = {};
  const individuais = [];
  processos.forEach(p => {
    if (p.etapa_padrao_id) {
      if (!gruposMap[p.etapa_padrao_id]) {
        const ep = etapasPadrao.find(e => e.id === p.etapa_padrao_id);
        gruposMap[p.etapa_padrao_id] = {
          nome:           ep?.titulo          ?? 'Etapa',
          requerRegistro: ep?.requer_registro ?? false,
          items: [],
        };
      }
      gruposMap[p.etapa_padrao_id].items.push(p);
    } else {
      individuais.push(p);
    }
  });
  const grupos = Object.entries(gruposMap);

  return (
    <div className={styles.fullscreenOverlay}>
      <div className={styles.fullscreenModal}>

        {/* ── Header ── */}
        <div className={styles.fullModalHeader}>
          <div className={styles.fullModalClienteInfo}>
            <span className={styles.tipoBadge}>{tipo}</span>
            <h2 className={styles.fullModalEmpresa}>{lead.empresa || lead.nome}</h2>
            {lead.empresa && <p className={styles.fullModalNome}>{lead.nome}</p>}
            {lead.telefone && (
              <p className={styles.fullModalTelefone}>
                <FiPhone size={11} style={{ marginRight: 4 }} />{lead.telefone}
              </p>
            )}
          </div>
          <div className={styles.fullModalHeaderRight}>
            {total > 0 && (
              <div className={styles.fullModalProgresso}>
                <div className={styles.fullModalProgressoBarra}>
                  <div
                    className={styles.fullModalProgressoPreenchida}
                    style={{ width: `${progresso}%` }}
                  />
                </div>
                <span className={styles.fullModalProgressoTexto}>{concluidos}/{total}</span>
              </div>
            )}
            <button className={styles.btnFechar} onClick={onFechar} aria-label="Fechar">
              <FiX />
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className={styles.fullModalTabBar}>
          <button
            type="button"
            className={`${styles.fullModalTab} ${abaAtiva === 'checklist' ? styles.fullModalTabAtiva : ''}`}
            onClick={() => setAbaAtiva('checklist')}
          >
            <FiCheckSquare size={14} />
            Checklist
            {total > 0 && <span className={styles.tabContador}>{total}</span>}
          </button>
          <button
            type="button"
            className={`${styles.fullModalTab} ${abaAtiva === 'info' ? styles.fullModalTabAtiva : ''}`}
            onClick={() => setAbaAtiva('info')}
          >
            <FiInfo size={14} />
            Informações
          </button>
        </div>

        {/* ── Body ── */}
        <div className={styles.fullModalBody}>

          {/* CHECKLIST */}
          {abaAtiva === 'checklist' && (
            <div className={styles.checklistWrapper}>
              {erro && <div className={styles.erroFaixa}>{erro}</div>}

              {carregando ? (
                <p className={styles.vazio}>Carregando processos...</p>
              ) : processos.length === 0 ? (
                <p className={styles.checklistVazio}>
                  Nenhum processo. Configure Etapas Padrão para auto-preencher.
                </p>
              ) : (
                <>
                  <div className={styles.checklistColunas}>
                    {grupos.map(([epId, grupo]) => {
                      const concluidosGrupo = grupo.items.filter(p => p.concluida).length;
                      return (
                        <div key={epId} className={styles.checklistColuna}>
                          <div className={styles.checklistColunaHeader}>
                            <span className={styles.checklistColunaTitulo}>{grupo.nome}</span>
                            {grupo.requerRegistro && (
                              <span className={styles.colunaRegistroTag} title="Exige registro ao concluir">
                                <FiClipboard size={10} />
                              </span>
                            )}
                            <span className={styles.checklistColunaCount}>
                              {concluidosGrupo}/{grupo.items.length}
                            </span>
                          </div>
                          <div className={styles.checklistColunaBody}>
                            {grupo.items.map(proc => {
                              const ep = etapasPadrao.find(e => e.id === proc.etapa_padrao_id);
                              const pp = ep?.processos?.find(p => p.id === proc.processo_padrao_id);
                              return (
                                <ChecklistItem
                                  key={proc.id}
                                  proc={proc}
                                  templateTexto={pp?.template_texto}
                                  abrirCadastro={pp?.abrir_cadastro ?? false}
                                  requerAnexo={pp?.requer_anexo ?? false}
                                  onToggle={handleToggle}
                                  onRemover={handleRemover}
                                  onVerDocumento={(titulo, tmpl) => setDocumento({ titulo, templateTexto: tmpl })}
                                  onAbrirCadastro={() => setCadastroAberto(true)}
                                  onAbrirAnexo={() => setAnexoProc(proc)}
                                />
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {individuais.length > 0 && (
                      <div className={styles.checklistColuna}>
                        <div className={styles.checklistColunaHeader}>
                          <span className={styles.checklistColunaTitulo}>Exclusivos</span>
                          <span className={styles.checklistColunaCount}>
                            {individuais.filter(p => p.concluida).length}/{individuais.length}
                          </span>
                        </div>
                        <div className={styles.checklistColunaBody}>
                          {individuais.map(proc => (
                            <ChecklistItem
                              key={proc.id}
                              proc={proc}
                              onToggle={handleToggle}
                              onRemover={handleRemover}
                              onVerDocumento={(titulo, tmpl) => setDocumento({ titulo, templateTexto: tmpl })}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={styles.checklistFooter}>
                    <div className={styles.etapaAddRow}>
                      <input
                        className={styles.etapaInput}
                        placeholder="Adicionar processo exclusivo deste cliente..."
                        value={novoProcesso}
                        onChange={e => setNovoProcesso(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdicionarCustom()}
                      />
                      <button
                        className={styles.btnPrimario}
                        onClick={handleAdicionarCustom}
                        disabled={salvando || !novoProcesso.trim()}
                      >
                        <FiPlus />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* INFORMAÇÕES */}
          {abaAtiva === 'info' && (
            <div className={styles.infoWrapper}>

              {/* ── Dados Cadastrais ── */}
              <div className={styles.detalheSecao}>
                <p className={styles.detalheSecaoTitulo}>Dados Cadastrais</p>
                <div className={styles.detalheGrid}>
                  <div className={styles.detalheCampo}>
                    <span className={styles.detalheLabel}><FiHash size={12} /> {lead.cnpj ? 'CNPJ' : 'CPF'}</span>
                    <span className={styles.detalheValor}>{lead.cnpj || lead.cpf || '—'}</span>
                  </div>
                  <div className={styles.detalheCampo}>
                    <span className={styles.detalheLabel}><FiPhone size={12} /> Telefone</span>
                    <span className={styles.detalheValor}>{lead.telefone || '—'}</span>
                  </div>
                  <div className={styles.detalheCampo}>
                    <span className={styles.detalheLabel}><FiMail size={12} /> E-mail</span>
                    <span className={styles.detalheValor}>{lead.email || '—'}</span>
                  </div>
                  <div className={styles.detalheCampo}>
                    <span className={styles.detalheLabel}><FiCalendar size={12} /> Convertido em</span>
                    <span className={styles.detalheValor}>{formatarData(lead.dataCadastro)}</span>
                  </div>
                </div>
              </div>

              {/* ── Negociação ── */}
              {(lead.servicosInteresse?.length > 0 || lead.servicosDetalhes?.length > 0 || lead.negociacao) && (
                <div className={styles.detalheSecao}>
                  <p className={styles.detalheSecaoTitulo}>Negociação</p>

                  {lead.servicosDetalhes?.length > 0 ? (
                    <div className={styles.infoServicosGrid}>
                      {lead.servicosDetalhes.map(s => (
                        <div key={s.tipo} className={styles.infoServicoItem}>
                          <span className={styles.infoServicoTipo}>{s.tipo}</span>
                          {s.valor ? (
                            <span className={styles.infoServicoValor}>
                              R$ {parseFloat(s.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className={styles.infoServicoSemValor}>—</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : lead.servicosInteresse?.length > 0 ? (
                    <div className={styles.servicos}>
                      {lead.servicosInteresse.map(s => (
                        <span key={s} className={styles.servicoBadge}>{s}</span>
                      ))}
                    </div>
                  ) : null}

                  {lead.negociacao && (
                    <p className={styles.detalheAnotacoes} style={{ marginTop: 12 }}>{lead.negociacao}</p>
                  )}
                </div>
              )}

              {/* ── Parâmetros Legais ── */}
              {(lead.regimeTributario || lead.tiposAtividade?.length > 0 || lead.porteEmpresa) && (
                <div className={styles.detalheSecao}>
                  <p className={styles.detalheSecaoTitulo}>Parâmetros Legais</p>
                  <div className={styles.detalheGrid}>

                    {lead.regimeTributario && (
                      <div className={styles.detalheCampo}>
                        <span className={styles.detalheLabel}>Regime Tributário</span>
                        <span className={styles.detalheValor}>{lead.regimeTributario}</span>
                      </div>
                    )}

                    {lead.regimeTributario === 'Simples Nacional' && lead.anexosSimples?.length > 0 && (
                      <div className={styles.detalheCampo}>
                        <span className={styles.detalheLabel}>Anexos Simples Nacional</span>
                        <div className={styles.servicos} style={{ marginTop: 4 }}>
                          {lead.anexosSimples.map(a => (
                            <span key={a} className={styles.servicoBadge}>Anexo {a}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {lead.tiposAtividade?.length > 0 && (
                      <div className={styles.detalheCampo}>
                        <span className={styles.detalheLabel}>Tipo de Atividade</span>
                        <div className={styles.servicos} style={{ marginTop: 4 }}>
                          {lead.tiposAtividade.map(t => (
                            <span key={t} className={styles.servicoBadge}>{t}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {lead.porteEmpresa && (
                      <div className={styles.detalheCampo}>
                        <span className={styles.detalheLabel}>Porte da Empresa</span>
                        <span className={styles.detalheValor}>{lead.porteEmpresa}</span>
                      </div>
                    )}
                  </div>

                  {/* Setores de Movimento */}
                  {lead.setoresMovimento && Object.values(lead.setoresMovimento).some(v => v) && (
                    <div style={{ marginTop: 14 }}>
                      <span className={styles.detalheLabel} style={{ display: 'block', marginBottom: 8 }}>Setores de Movimento</span>
                      <div className={styles.infoSetoresGrid}>
                        {Object.entries(lead.setoresMovimento).map(([setor, valor]) => valor ? (
                          <div key={setor} className={styles.infoSetorItem}>
                            <span className={styles.infoSetorNome}>{setor}</span>
                            <span className={`${styles.infoSetorValor} ${valor === 'Com Movimento' ? styles.infoSetorCom : styles.infoSetorSem}`}>
                              {valor}
                            </span>
                          </div>
                        ) : null)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Obrigações ── */}
              {lead.obrigacoes?.length > 0 && (
                <div className={styles.detalheSecao}>
                  <p className={styles.detalheSecaoTitulo}>Obrigações</p>
                  <div className={styles.infoObrigacoesList}>
                    {lead.obrigacoes.map((o, i) => (
                      <div key={i} className={styles.infoObrigacaoItem}>
                        <span className={styles.infoObrigacaoNome}>{o.nome}</span>
                        {o.descricao && <span className={styles.infoObrigacaoDesc}>{o.descricao}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Anotações legado ── */}
              {lead.anotacoes && !lead.negociacao && (
                <div className={styles.detalheSecao}>
                  <p className={styles.detalheSecaoTitulo}>Anotações</p>
                  <p className={styles.detalheAnotacoes}>{lead.anotacoes}</p>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {documento && (
        <DocumentoModal
          titulo={documento.titulo}
          templateTexto={documento.templateTexto}
          lead={lead}
          onFechar={() => setDocumento(null)}
        />
      )}

      {registroModal && (
        <RegistroModal
          processo={registroModal.proc}
          lead={lead}
          onSalvar={handleSalvarRegistro}
          onCancelar={() => setRegistroModal(null)}
        />
      )}

      {cadastroAberto && (
        <CadastroClienteModal
          lead={lead}
          onFechar={() => setCadastroAberto(false)}
        />
      )}

      {anexoProc && (
        <AnexoModal
          proc={anexoProc}
          lead={lead}
          onFechar={() => setAnexoProc(null)}
          onSalvo={handleAnexoSalvo}
        />
      )}
    </div>
  );
}

export default DetalheModal;
